import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faCloudUploadAlt, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { uploadService } from '../../services/uploadService';

export default function SubmitFlyer() {
  const [form, setForm] = useState({ title: '', description: '', data: '', mimeType: '' });
  const [previewUrl, setPreviewUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  function resetForm() {
    setForm({ title: '', description: '', data: '', mimeType: '' });
    setPreviewUrl('');
  }

  function handleFileChange(file) {
    if (!file) {
      setForm(prev => ({ ...prev, data: '', mimeType: '' }));
      setPreviewUrl('');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      setForm(prev => ({ ...prev, data: dataUrl, mimeType: file.type || '' }));
      setPreviewUrl(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSuccessMsg('');
    setErrorMsg('');
    try {
      await uploadService.createPublic(form);
      setSuccessMsg('Thank you! Your flyer was submitted for review.');
      resetForm();
    } catch (e) {
      setErrorMsg(e.message || 'Failed to submit flyer');
    } finally {
      setSubmitting(false);
    }
  }

  const isImage = (type) => String(type || '').startsWith('image/');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center mb-4">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-3">
              <FontAwesomeIcon icon={faCloudUploadAlt} />
            </div>
            <h1 className="text-2xl font-bold">Submit a Flyer</h1>
          </div>
          <p className="text-gray-600 mb-6">
            Upload your promotional image or flyer. Submissions are reviewed by admin before appearing on the home page.
          </p>

          {successMsg && (
            <div className="mb-4 px-4 py-3 bg-green-50 text-green-700 border border-green-200 rounded flex items-center">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
              <span>{successMsg}</span>
            </div>
          )}
          {errorMsg && (
            <div className="mb-4 px-4 py-3 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded">
              {errorMsg}
            </div>
          )}

          <form onSubmit={submit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  value={form.title}
                  onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border rounded p-2"
                  placeholder="e.g., Black Friday Promo"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border rounded p-2"
                  rows={3}
                  placeholder="Brief description (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={e => handleFileChange(e.target.files?.[0] || null)}
                  className="w-full"
                  required
                />
                {previewUrl && (
                  <div className="mt-3">
                    {isImage(form.mimeType) ? (
                      <img src={previewUrl} alt="Preview" className="w-48 h-48 object-cover rounded" />
                    ) : (
                      <div className="w-48 h-48 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                        <FontAwesomeIcon icon={faFile} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 flex items-center space-x-3">
              <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                {submitting ? 'Submitting...' : 'Submit Flyer'}
              </button>
              <a href="/" className="px-4 py-2 rounded border">Back to Home</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}